import './StockInfoCard.scss';

import type { FieldValues, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import React, { useEffect, useState } from 'react';
import SwitchColorButton from '../switchColorButtons/SwitchColorButton';
import {
  formatDateToMonthDay,
  formatListWithAmpersand,
  getMaxRestockDate,
} from '@/utilities/stockUtils';

import type { ITimelineElement } from '@/common/interfaces/cart.interface';
import type { RootState } from '@/store';
import { getNewTimelineShippingRanges } from '@/utilities/CreateOrderUtils';
import { useStocksWithColorFallback } from '@/hooks/useStocksWithColorFallback';
import { useAppSelector } from '@/store/hooks';
import { useFeatureFlag } from '@/hooks/useAccount';
import { useGetTimelineMutation } from '@/api/timelineQuery';

type StockInfoCardProps = Readonly<{
  proofItemId?: string | number;
  styleCode: string;
  colorCode: string;
  apparelColorCode?: string;
  designTool?: boolean;
  formIndex?: number;
  getValues?: UseFormGetValues<FieldValues>;
  setValue?: UseFormSetValue<FieldValues>;
  color?: string;
  source?: string;
  productBrand?: string;
  section?: string;
  seeMoreUrl?: string;
  isStoreCart?: boolean;
  hideSwitchOptions?: boolean;
}>;

export default function StockInfoCard({
  proofItemId,
  styleCode,
  colorCode,
  apparelColorCode = '',
  designTool = false,
  formIndex,
  getValues,
  setValue,
  color = '',
  source = 'HQ',
  productBrand = '',
  section = 'HQ Cart',
  seeMoreUrl = '',
  isStoreCart = false,
  hideSwitchOptions = false,
}: StockInfoCardProps) {
  const { stockData } = useStocksWithColorFallback({
    styleCode,
    apparelColorCode: apparelColorCode || colorCode,
    apparelColorHex: colorCode,
    skip: !styleCode?.trim(),
  });
  const [getTimeline] = useGetTimelineMutation();
  const [timelineData, setTimelineData] = useState<{
    maxTimeline?: ITimelineElement[];
    shippingTimelines?: ITimelineElement[];
  } | null>(null);
  // Call timeline API
  const { user } = useAppSelector((state: RootState) => state.user);
  const timelineChangesEnabled = useFeatureFlag(user, 'TIMELINE_GUARANTEE_CHANGES');
  useEffect(() => {
    if (isStoreCart || !proofItemId) {
      return;
    }

    const fetchTimeline = async () => {
      try {
        const timelinePayload = {
          proofItemData: [{ proofItemId }],
          fetchMaxTimeline: true,
        };

        const timelineResponse = await getTimeline({ data: timelinePayload });

        if ('data' in timelineResponse) {
          setTimelineData(timelineResponse.data);
        }
      } catch (error) {
        console.error('Timeline API error:', error);
      }
    };

    fetchTimeline();
  }, [styleCode, colorCode, getTimeline, isStoreCart, proofItemId]);

  const showSwitchOptions = designTool && !hideSwitchOptions;

  const coreSizes = stockData.filter((item) => item.core_size).map((item) => item.size);
  const soldOutSizes = stockData
    .filter((item) => item.status === 'outOfStock')
    .map((item) => item.size);
  const belowThresholdCoreSize = stockData
    .filter((item) => coreSizes.includes(item.size) && item.status === 'lowInStock')
    .map((item) => item.size);
  const soldOutCore = stockData
    .filter((item) => coreSizes.includes(item.size) && item.status === 'outOfStock')
    .map((item) => item.size);

  const restockDate = getMaxRestockDate(stockData);

  useEffect(() => {
    if (
      !stockData ||
      !Array.isArray(stockData) ||
      setValue === undefined ||
      getValues === undefined ||
      stockData.length === 0
    ) {
      return;
    }

    const allSoldOut = soldOutSizes.length === stockData.length && !restockDate;

    if (typeof formIndex === 'number') {
      const currentValue = getValues?.(`cartDetails.${formIndex}.outOfStock`);
      if (currentValue !== allSoldOut) {
        setValue?.(`cartDetails.${formIndex}.outOfStock`, allSoldOut);
      }
    }
  }, [stockData, formIndex, setValue, getValues, restockDate, soldOutSizes.length]);

  if (!stockData || !Array.isArray(stockData)) return null;

  const sizes: { [key: string]: number } = {};
  stockData.forEach((item) => {
    sizes[item.size] = +item.qty;
  });

  const rushDeliveryDate = getNewTimelineShippingRanges(
    timelineData?.maxTimeline ?? timelineData?.shippingTimelines ?? [],
    'expedited',
    true,
    restockDate ?? '',
    false,
    false,
    false,
    timelineChangesEnabled
  );

  const boldList = (items: string[]) => {
    // Create array of formatted size strings with quantities
    const formattedItems = items.map((sz) => {
      const qty = sizes[sz];
      return `${qty} ${sz}${qty > 1 ? 's' : ''}`;
    });

    // Use proper comma separation with & at the end
    if (formattedItems.length === 0) return '';
    if (formattedItems.length === 1) return <strong>{formattedItems[0]}</strong>;
    if (formattedItems.length === 2) {
      return (
        <>
          <strong>{formattedItems[0]}</strong> & <strong>{formattedItems[1]}</strong>
        </>
      );
    }

    return (
      <>
        {formattedItems.slice(0, -1).map((item, index) => (
          <span key={`item-${index}`}>
            <strong>{item}</strong>
            {index < formattedItems.length - 2 ? ', ' : ' & '}
          </span>
        ))}
        <strong>{formattedItems[formattedItems.length - 1]}</strong>
      </>
    );
  };

  const getEmoji = () => {
    if (soldOutSizes.length === stockData.length) return <span>&#128565;</span>;
    if (soldOutCore.length > 0) return <span>😱</span>;
    return <span>🫣</span>;
  };

  const getCompletelySoldOutMessage = () => {
    // Check if all sizes have no restock date
    const allNoRestockDate = stockData.every((item) => !item.restock_date);
    if (allNoRestockDate) {
      return (
        <>
          {getEmoji()} This color is <strong>completely sold out</strong> with no restock date.
          We&apos;ll email you as soon as it&apos;s back.
        </>
      );
    }

    // Group sizes by restock date
    const groupedByDate = stockData.reduce((acc, curr) => {
      const date = curr.restock_date || 'no_date';
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(curr.size);
      return acc;
    }, {} as { [key: string]: string[] });

    // Generate restock message based on groups, sorting to ensure 'no_date' comes last
    const messages = Object.entries(groupedByDate)
      .sort(([dateA], [dateB]) => {
        if (dateA === 'no_date') return 1;
        if (dateB === 'no_date') return -1;
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      })
      .map(([date, sizeList]) => {
        const formattedSizes = formatListWithAmpersand(sizeList);
        if (date === 'no_date') {
          return `${formattedSizes} ${sizeList.length === 1 ? 'has' : 'have'} no restock date yet`;
        }
        return `${formattedSizes} ${
          sizeList.length === 1 ? 'is' : 'are'
        } restocking on ${formatDateToMonthDay(date)}`;
      });

    const restockMessage = formatListWithAmpersand(messages);
    const delivery = rushDeliveryDate
      ? ` Estimated delivery by ${rushDeliveryDate} with rush shipping.`
      : '';

    return (
      <>
        <span>&#128565;</span> This color is <strong>completely sold out</strong>. {restockMessage}.
        {delivery}
      </>
    );
  };

  const getCoreStockMessage = () => {
    if (belowThresholdCoreSize.length) {
      return (
        <>
          and <strong>only has {boldList(belowThresholdCoreSize)} left.</strong>
        </>
      );
    }
    if (!soldOutCore.length && !coreSizes.some((size) => soldOutSizes.includes(size))) {
      return 'and selling out fast in other sizes.';
    }
    return '';
  };

  const getRestockMessage = () => {
    if (soldOutCore.length || (soldOutSizes.length > 0 && belowThresholdCoreSize.length > 0)) {
      // Get individual stock data for each sold out and low stock size
      const sizeData = [...soldOutSizes, ...belowThresholdCoreSize].map((size) => {
        const stockItem = stockData.find((item) => item.size === size);
        return {
          size,
          restock_date: stockItem?.restock_date,
          isLowStock: belowThresholdCoreSize.includes(size),
        };
      });

      // Group sizes by restock date
      const groupedByDate = sizeData.reduce((acc, curr) => {
        const date = curr.restock_date || 'no_date';
        if (!acc[date]) {
          acc[date] = { sizeList: [], isLowStock: [] };
        }
        acc[date].sizeList.push(curr.size);
        acc[date].isLowStock.push(curr.isLowStock);
        return acc;
      }, {} as { [key: string]: { sizeList: string[]; isLowStock: boolean[] } });

      // Sort entries so that restock dates come before 'no_date'
      const sortedEntries = Object.entries(groupedByDate).sort(([dateA], [dateB]) => {
        if (dateA === 'no_date') return 1;
        if (dateB === 'no_date') return -1;
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      });

      // If all sizes have the same restock date
      if (sortedEntries.length === 1) {
        const [date] = sortedEntries[0];
        if (date === 'no_date') {
          return ' No restock date yet.';
        }
        return ` Restocking on ${formatDateToMonthDay(date)}.`;
      }

      // If sizes have different restock dates
      const messages = sortedEntries.map(([date, { sizeList, isLowStock }]) => {
        const formattedSizes = formatListWithAmpersand(sizeList);
        const allLowStock = isLowStock.every(Boolean);

        let verb = 'are';
        if (sizeList.length === 1) {
          verb = allLowStock ? 'will be' : 'is';
        } else {
          verb = allLowStock ? 'will be' : 'are';
        }

        if (date === 'no_date') {
          return `${formattedSizes} ${sizeList.length === 1 ? 'has' : 'have'} no restock date yet`;
        }
        return `${formattedSizes} ${verb} restocking on ${formatDateToMonthDay(date)}`;
      });

      return ` ${formatListWithAmpersand(messages)}.`;
    }
    return '';
  };

  const getDeliveryMessage = () => {
    if (isStoreCart) {
      return '';
    }

    // Check if any size has no restock date
    const hasNoRestockDate = soldOutSizes.some((size) => {
      const stockItem = stockData.find((item) => item.size === size);
      return !stockItem?.restock_date;
    });

    if (hasNoRestockDate) {
      return ` Order now before they're all gone!`;
    }

    if (rushDeliveryDate) {
      return ` Estimated delivery by ${rushDeliveryDate} with rush shipping.`;
    }
    return '';
  };

  const renderMessage = () => {
    // Special case: non-core sizes out of stock AND core sizes with stock
    if (
      soldOutSizes.length > 0 &&
      soldOutSizes.length < stockData.length &&
      !soldOutCore.length &&
      belowThresholdCoreSize.length > 0
    ) {
      return (
        <>
          This color is sold out in <strong>{formatListWithAmpersand(soldOutSizes)}</strong> and
          <strong> only has {boldList(belowThresholdCoreSize)}</strong> left.
          {!isStoreCart && <> Order now before they&apos;re all gone.</>}
        </>
      );
    }

    // All other existing scenarios remain the same
    if (soldOutSizes.length > 0 && soldOutSizes.length < stockData.length) {
      // Only show restock message if there are core sizes sold out
      const shouldShowRestockMessage = soldOutCore.length > 0;
      return (
        <>
          {getEmoji()} This color is{' '}
          <strong>
            {' '}
            sold out in {formatListWithAmpersand(soldOutSizes)}
            {belowThresholdCoreSize.length === 0 ? '.' : ''}
          </strong>{' '}
          {getCoreStockMessage()}
          {shouldShowRestockMessage ? getRestockMessage() : ''} {getDeliveryMessage()}
        </>
      );
    }

    if (soldOutSizes.length === stockData.length) {
      return <>{getCompletelySoldOutMessage()}</>;
    }

    if (belowThresholdCoreSize.length > 0) {
      const totalQty = belowThresholdCoreSize.reduce((sum, size) => sum + sizes[size], 0);
      const isSingular = totalQty === 1;
      return (
        <>
          {getEmoji()} There {isSingular ? 'is' : 'are'} only{' '}
          <strong>{boldList(belowThresholdCoreSize)}</strong> left in this color.
          {!isStoreCart && <> Order now before they&apos;re all gone.</>}
        </>
      );
    }

    return null;
  };

  const getPlainTextMessage = (jsxMessage: React.ReactNode): string => {
    if (!jsxMessage) return '';

    const extractText = (node: React.ReactNode): string => {
      if (typeof node === 'string') return node;
      if (typeof node === 'number') return node.toString();
      if (Array.isArray(node)) return node.map(extractText).join('');
      if (typeof node === 'object' && node && 'props' in node) {
        const element = node as React.ReactElement;
        if (element.props.children) {
          return extractText(element.props.children);
        }
      }
      return '';
    };

    const plainText = extractText(jsxMessage);
    return plainText;
  };

  const message = renderMessage();

  const isWarningState = () => {
    // Case 1: Only low stock (no sold out items)
    if (belowThresholdCoreSize.length > 0 && soldOutSizes.length === 0) {
      return true;
    }
    // Case 2: Non-core sizes out of stock but core sizes available
    if (soldOutSizes.length > 0 && soldOutSizes.length < stockData.length && !soldOutCore.length) {
      return true;
    }
    return false;
  };

  return stockData.length > 0 && message ? (
    <div className="stock-info-container">
      <div className={`stock-info${isWarningState() ? ' stock-info--warning' : ''}`}>
        <div
          className={`stock-message${isWarningState() ? ' stock-message--warning' : ''}`}
          style={!showSwitchOptions ? { borderBottom: '0px', borderRadius: '10px' } : undefined}
        >
          <p>{renderMessage()}</p>
        </div>
        {showSwitchOptions && (
          <SwitchColorButton
            label="Or"
            productStyleCode={styleCode}
            color={color}
            source={source}
            message={getPlainTextMessage(message)}
            productBrand={productBrand}
            section={section}
            seeMoreUrl={seeMoreUrl}
            hexCode={colorCode}
            proofItemId={proofItemId}
          />
        )}
      </div>
    </div>
  ) : null;
}
